/**
 * Coloured circular avatar for a group-cart participant.
 * Shows the participant's first initial + a verified-tick badge if they've
 * authenticated with the customer JWT.
 */
import type { GroupCartParticipant } from "@/api/group-cart";
import { colorFor } from "@/lib/participant-colors";

export function initialFor(p: Pick<GroupCartParticipant, "displayName">): string {
  const ch = (p.displayName ?? "").trim().charAt(0).toUpperCase();
  return ch || "?";
}

export interface AvatarProps {
  participant: GroupCartParticipant;
  size?: number;
  /** Outer ring colour (used by AvatarStack on overlap). */
  ring?: string;
  /** Render the small verified tick. */
  showVerified?: boolean;
}

export function Avatar({
  participant,
  size = 32,
  ring,
  showVerified = false,
}: AvatarProps) {
  const color = colorFor(participant.colorIndex);
  const fontSize = Math.max(11, Math.round(size * 0.42));
  const tick = Math.max(12, Math.round(size * 0.34));
  return (
    <div
      style={{ position: "relative", width: size, height: size, flexShrink: 0 }}
      aria-label={participant.displayName}
      role="img"
    >
      <div
        style={{
          width: size,
          height: size,
          borderRadius: 999,
          background: color.soft,
          color: color.fg,
          display: "grid",
          placeItems: "center",
          fontWeight: 700,
          fontSize,
          letterSpacing: -0.2,
          boxShadow: ring ? `0 0 0 2px ${ring}` : "none",
          border: `1.5px solid ${color.fg}33`,
        }}
      >
        {initialFor(participant)}
      </div>
      {showVerified && participant.verified && (
        <div
          style={{
            position: "absolute",
            right: -2,
            bottom: -2,
            width: tick,
            height: tick,
            borderRadius: 999,
            background: "var(--primary-600)",
            border: "2px solid var(--surface-1)",
            display: "grid",
            placeItems: "center",
          }}
          aria-hidden
        >
          <svg
            width={Math.max(7, size * 0.18)}
            height={Math.max(7, size * 0.18)}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12l4.5 4.5L19 7" />
          </svg>
        </div>
      )}
    </div>
  );
}
